import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import axios from 'axios';
import './FlowLayout.css';

/* Group locations by state */
function groupByState(locations) {
  const stateMap = {};
  locations.forEach((loc) => {
    const state = loc.state && loc.state.trim() !== '' ? loc.state : 'Other';
    if (!stateMap[state]) stateMap[state] = [];
    stateMap[state].push(loc);
  });
  return stateMap;
}

function SignUpPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [allLocations, setAllLocations] = useState([]);
  const [stateMap, setStateMap] = useState({});
  const [selectedState, setSelectedState] = useState('');
  const [selectedLocation, setSelectedLocation] = useState('');
  const [selectedLocations, setSelectedLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const API_ENDPOINT = process.env.REACT_APP_API_ENDPOINT;

  useEffect(() => {
    const fetchLocations = async () => {
      try {
        const response = await axios.get(`${API_ENDPOINT}/api/locations/`);
        const locationOptions = response.data.map((location) => ({
          value: String(location.location_id),
          label: location.location_name,
          state: location.state,
          address: location.address
        }));
        setAllLocations(locationOptions);
        setStateMap(groupByState(locationOptions));
      } catch (error) {
        console.error('Error fetching locations:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchLocations();
  }, []);

  const states = Object.keys(stateMap).sort();
  const locationsForState = selectedState ? stateMap[selectedState] || [] : [];

  const handleAddLocation = () => {
    if (!selectedLocation) return;
    if (selectedLocations.length >= 3) return;
    const loc = allLocations.find(l => l.value === selectedLocation);
    if (loc && !selectedLocations.some(l => l.value === loc.value)) {
      setSelectedLocations([...selectedLocations, loc]);
      setSelectedLocation('');
      setSelectedState('');
    }
  };

  const handleRemoveLocation = (val) => {
    setSelectedLocations(selectedLocations.filter(l => l.value !== val));
  };

  const handleSignUp = async (event) => {
    event.preventDefault();
    if (!email || selectedLocations.length === 0) {
      setError('Email and at least one location are required.');
      return;
    }

    setSubmitting(true);
    setError('');

    const subscribedLocations = selectedLocations.map(location => location.value);

    try {
      const response = await axios.post(`${API_ENDPOINT}/api/register/`, {
        email,
        subscribed_locations: subscribedLocations,
        product: 'NOTIFY'
      });
      window.location.href = response.data.url;
    } catch (err) {
      console.error('Error during registration:', err);
      setError('An error occurred. Please try again.');
      setSubmitting(false);
    }
  };

  return (
    <div className="flow-page">
      <Helmet>
        <title>Get Appointment Alerts</title>
        <meta name="description" content="Sign up for real-time Global Entry appointment alerts." />
      </Helmet>

      {/* Progress Bar */}
      <div className="progress-bar-container" role="progressbar" aria-valuenow={2} aria-valuemin={1} aria-valuemax={3} aria-label="Step 2 of 3: Details">
        <div className="progress-bar-header">
          <button className="flow-back" onClick={() => { navigate('/get-started'); window.scrollTo(0, 0); }}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6" /></svg>
            Back
          </button>
          <div className="progress-bar-label">Step 2 of 3: Details</div>
        </div>
        <div className="progress-bar-track">
          <div className="progress-bar-fill" style={{ width: '66%' }} />
        </div>
      </div>

      <div className="flow-layout">
        <div className="flow-left">
          <h1 className="flow-heading">
            Where should we send your alerts?
          </h1>
          <p className="flow-subtitle">
            Enter your email and select up to 3 locations you'd like us to monitor.
          </p>

          <div className="info-card info-card--subtle" style={{ marginTop: '48px' }}>
            <h3 className="info-card__title">How it works</h3>
            <p className="info-card__text">
              We monitor the official system 24/7. As soon as a slot opens at any of your chosen locations, 
              we'll send you an instant email with a direct link to book.
            </p>
          </div>
        </div>

        <div className="flow-right">
          <div className="panel-card">
            <div style={{ marginBottom: '24px' }}>
              <label className="flow-label" htmlFor="email-input">Email Address</label>
              <input
                id="email-input"
                className="connect-input"
                type="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={submitting}
              />
            </div>

            <div style={{ marginBottom: '32px' }}>
              <label className="flow-label">Selected Locations ({selectedLocations.length}/3)</label>
              {selectedLocations.length > 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '8px', marginBottom: '16px' }}>
                  {selectedLocations.map(loc => (
                    <div key={loc.value} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 14px', backgroundColor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '8px' }}>
                      <span style={{ fontSize: '0.95rem', fontWeight: '500', color: '#1e293b' }}>{loc.label}</span>
                      <button 
                        onClick={() => handleRemoveLocation(loc.value)}
                        style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', padding: '4px' }}
                      >
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <p style={{ fontSize: '0.875rem', color: '#64748b', marginTop: '8px', marginBottom: '16px' }}>No locations selected yet.</p>
              )}

              {selectedLocations.length < 3 && (
                <div style={{ padding: '16px', backgroundColor: '#f1f5f9', borderRadius: '8px' }}>
                  <div style={{ marginBottom: '12px' }}>
                    <select
                      className="flow-select"
                      value={selectedState}
                      onChange={(e) => { setSelectedState(e.target.value); setSelectedLocation(''); }}
                      style={{ backgroundColor: '#fff', marginBottom: '8px' }}
                    >
                      <option value="" disabled hidden>Select State</option>
                      {states.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                    
                    <select
                      className="flow-select"
                      value={selectedLocation}
                      onChange={(e) => setSelectedLocation(e.target.value)}
                      disabled={!selectedState}
                      style={{ backgroundColor: '#fff' }}
                    >
                      <option value="" disabled hidden>Select Center</option>
                      {locationsForState.filter(l => !selectedLocations.some(sl => sl.value === l.value)).map(l => (
                        <option key={l.value} value={l.value}>{l.label}</option>
                      ))}
                    </select>
                  </div>
                  <button 
                    className="flow-btn-primary" 
                    onClick={handleAddLocation} 
                    disabled={!selectedLocation}
                    style={{ width: '100%', padding: '10px', fontSize: '0.9rem' }}
                  >
                    Add Location
                  </button>
                </div>
              )}
            </div>

            {error && (
              <div style={{ color: '#ef4444', fontSize: '0.875rem', marginBottom: '16px' }}>{error}</div>
            )}

            <button
              className="flow-btn-primary"
              onClick={handleSignUp}
              disabled={submitting || !email || selectedLocations.length === 0}
              style={{ width: '100%' }}
            >
              {submitting ? 'Preparing Checkout...' : 'Continue to Payment'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default SignUpPage;
